import type {
  AdjustablePlacement,
  CampaignBidAdjustments,
  CampaignBiddingUpdate,
  CampaignType,
  PlacementKey,
  PlacementMetrics,
} from "@adpilot/shared";
import {
  IconChartLine,
  IconDownload,
  IconPencil,
  IconPercentage,
  IconStack2,
} from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { csvDocument, downloadCsv } from "@/components/campaign/ad-groups/ad-groups-table-model";
import { CampaignBiddingStrategyCell } from "@/components/campaign/CampaignBiddingStrategyCell";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Checkbox } from "@/components/ui/checkbox";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "@/components/ui/menu";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/api-error";
import { fetchCampaignBidAdjustments } from "@/lib/campaign-api";
import { updateCampaignBidding } from "@/lib/campaign-settings-api";
import { useFormatters } from "@/lib/format";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { CampaignDetailPerformance } from "../performance/CampaignDetailPerformance";
import { BiddingStrategyPopover } from "./BiddingStrategyPopover";
import { totalOf } from "./bid-adjustment-model";
import {
  PlacementAdjustmentDialog,
  type PlacementAdjustmentField,
} from "./PlacementAdjustmentDialog";
import { PlacementDetailDialog } from "./PlacementDetailDialog";

const SUB_TABS = ["placement", "audience", "amazon_business"] as const;
type SubTab = (typeof SUB_TABS)[number];

/** Reference column order after the name, strategy and adjustment columns. */
const METRIC_COLUMNS = [
  "cpc",
  "cpa",
  "ctr",
  "cvr",
  "acos",
  "roas",
  "spend",
  "impressions",
  "clicks",
  "orders",
  "sales",
] as const satisfies readonly (keyof PlacementMetrics)[];
type MetricColumn = (typeof METRIC_COLUMNS)[number];
const MONEY = new Set<MetricColumn>(["cpc", "cpa", "spend", "sales"]);
const PERCENT = new Set<MetricColumn>(["ctr", "cvr", "acos"]);

interface Row {
  key: string;
  name: string;
  /** Opens the placement's 数据详情; absent where there is no report row. */
  placement?: PlacementKey;
  adjustment: number | null;
  metrics: PlacementMetrics | null;
  /** Set where the adjustment can be edited (not off-Amazon, not audiences). */
  adjustable?: AdjustablePlacement;
}

export interface CampaignBidAdjustmentProps {
  campaignId: string;
  shopId: string | null;
  campaignType: CampaignType;
  range: ReportingPeriod;
  timeZone: ReportingTimeZone;
  /** May change bidding on Amazon (the same gate as the other campaign edits). */
  canWrite: boolean;
}

/** 竞价调整: the campaign's bidding strategy and adjustments beside placement performance. */
export function CampaignBidAdjustment({
  campaignId,
  shopId,
  campaignType,
  range,
  timeZone,
  canWrite,
}: CampaignBidAdjustmentProps) {
  const { t } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.bidAdjustment.${key}`, options);
  const { formatCurrency, formatNumber, formatPercent } = useFormatters();
  const [subTab, setSubTab] = useState<SubTab>("placement");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<PlacementKey | null>(null);
  // The last opened placement, kept while the dialog animates closed.
  const [lastDetail, setLastDetail] = useState<PlacementKey | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<AdjustablePlacement>>(new Set());
  // Kept after closing (open: false) so the dialog keeps its content while it animates out.
  const [editing, setEditing] = useState<{
    open: boolean;
    title: string;
    batch: boolean;
    fields: PlacementAdjustmentField[];
  } | null>(null);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (input: CampaignBiddingUpdate) => updateCampaignBidding(campaignId, input),
    onSuccess: (result) => {
      toast.success(text(result.changed ? "edit.saved" : "edit.unchanged"));
      setEditing((current) => (current ? { ...current, open: false } : current));
      setSelected(new Set());
      // The tab and the header's strategy both read this campaign.
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error));
      // An ambiguous write may still have landed: show what is stored now.
      void queryClient.invalidateQueries({
        queryKey: ["campaign", campaignId, "bid-adjustments"],
      });
    },
  });

  const dates = resolveReportingPeriod(range, new Date(), timeZone);
  const isSp = campaignType === "sp";
  const query = useQuery({
    queryKey: ["campaign", campaignId, "bid-adjustments", dates.startDate, dates.endDate],
    queryFn: ({ signal }) => fetchCampaignBidAdjustments(campaignId, dates, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: isSp,
  });
  const data = query.data?.data;
  const currency = data?.currency ?? "USD";

  if (!isSp) {
    return (
      <div className="rounded-[14px] border border-line bg-surface p-3.5">
        <EmptyState
          icon={<IconPercentage className="size-12" />}
          title={text("spOnlyTitle")}
          description={text("spOnly")}
        />
      </div>
    );
  }

  const rowsOf = (bidding: CampaignBidAdjustments): Row[] => {
    if (subTab === "placement") {
      return bidding.placements.map(({ placement, adjustment, ...metrics }) => ({
        key: placement,
        name: text(`placements.${placement}`),
        // As in the reference, off-Amazon (reported, not adjustable) has no 数据详情.
        placement: placement === "off_amazon" ? undefined : placement,
        adjustment,
        metrics,
        adjustable: placement === "off_amazon" ? undefined : placement,
      }));
    }
    if (subTab === "amazon_business") {
      return bidding.amazonBusiness.available
        ? [
            {
              key: "amazon_business",
              name: text("placements.amazon_business"),
              adjustment: bidding.amazonBusiness.adjustment,
              // Amazon's placement report has no Amazon Business row.
              metrics: null,
              adjustable: "amazon_business" as const,
            },
          ]
        : [];
    }
    return bidding.audiences.map((audience) => ({
      key: audience.audienceId,
      name: audience.audienceId,
      adjustment: audience.adjustment,
      metrics: null,
    }));
  };
  const term = search.trim().toLowerCase();
  const rows = data ? rowsOf(data).filter((row) => row.name.toLowerCase().includes(term)) : [];
  const metricRows = rows.flatMap((row) => (row.metrics ? [row.metrics] : []));
  const total = metricRows.length > 0 ? totalOf(metricRows) : null;

  const metricText = (metrics: PlacementMetrics | null, column: MetricColumn) => {
    const value = metrics?.[column];
    if (value == null) return "--";
    if (MONEY.has(column)) return formatCurrency(value, currency);
    if (PERCENT.has(column)) return formatPercent(value, 2);
    return column === "roas"
      ? formatNumber(value)
      : formatNumber(value, { maximumFractionDigits: 0 });
  };
  const adjustmentText = (value: number | null) =>
    value == null ? "--" : formatPercent(value / 100, 2);
  const strategyLabel = data?.biddingStrategy
    ? t(`list.biddingStrategy.${data.biddingStrategy}`)
    : "--";

  const nameHeader = text(`columns.name.${subTab}`);
  const exportCsv = () => {
    downloadCsv(
      `campaign-${campaignId}-bid-adjustments-${subTab}-${dates.startDate}_${dates.endDate}.csv`,
      csvDocument([
        [
          nameHeader,
          text("columns.strategy"),
          text("columns.adjustment"),
          ...METRIC_COLUMNS.map((column) => text(`columns.${column}`)),
        ],
        ...rows.map((row) => [
          row.name,
          strategyLabel,
          adjustmentText(row.adjustment),
          ...METRIC_COLUMNS.map((column) => metricText(row.metrics, column)),
        ]),
      ]),
    );
  };

  // Edits need the synced bidding to start from, and write access.
  const editable = canWrite && !!data?.biddingSyncedAt && !query.isPlaceholderData;
  const adjustableRows = rows.filter((row) => row.adjustable);
  const fieldOf = (row: Row): PlacementAdjustmentField => ({
    placement: row.adjustable as AdjustablePlacement,
    label: row.name,
    value: row.adjustment,
  });
  const openBatch = () =>
    // Like the reference, the batch form offers every adjustable placement of
    // the sub-tab; an empty field keeps its value.
    setEditing({
      open: true,
      title: text("edit.batchTitle"),
      batch: true,
      fields: adjustableRows.map((row) => ({ ...fieldOf(row), value: null })),
    });
  const allSelected =
    adjustableRows.length > 0 && adjustableRows.every((row) => selected.has(row.adjustable!));

  const toolbar = (
    <>
      <div className="flex items-center gap-3">
        {editable && subTab !== "audience" ? (
          <Menu>
            <MenuTrigger
              render={
                <ControlButton variant="outline" disabled={selected.size === 0 || save.isPending} />
              }
            >
              <IconStack2 />
              {text("edit.batch")}
            </MenuTrigger>
            <MenuPopup align="start">
              <MenuItem onClick={openBatch}>{text("edit.adjustBids")}</MenuItem>
            </MenuPopup>
          </Menu>
        ) : null}
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          {data?.biddingSyncedAt ? text("syncedHint") : text("notSyncedYet")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label={text("search")}
          placeholder={text("search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => setSearch(event.target.value)}
        />
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={text("export")}
          title={text("export")}
          disabled={rows.length === 0 || query.isPlaceholderData}
          onClick={exportCsv}
        >
          <IconDownload />
        </ControlButton>
      </div>
    </>
  );

  // 站点不支持企业购是永久事实，优先于「尚未同步」提示。
  const emptyKey =
    subTab === "amazon_business" && data && !data.amazonBusiness.available
      ? "amazonBusinessUnavailable"
      : data && !data.biddingSyncedAt && subTab !== "placement"
        ? "notSynced"
        : subTab === "audience"
          ? "emptyAudience"
          : "empty";

  const body = query.isPending ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={text("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : query.isError && !data ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    </div>
  ) : rows.length === 0 ? (
    <div className="p-3.5">
      <EmptyState
        icon={<IconPercentage className="size-12" />}
        title={text(`${emptyKey}.title`)}
        description={text(`${emptyKey}.description`)}
      />
    </div>
  ) : (
    <DataTable density="compact" stickyHeader aria-label={text(`subTabs.${subTab}`)}>
      <TableHeader>
        <TableRow>
          {editable && subTab !== "audience" ? (
            <TableHead className="w-10">
              <Checkbox
                aria-label={text("edit.selectAll")}
                checked={allSelected}
                indeterminate={selected.size > 0 && !allSelected}
                onCheckedChange={(checked) =>
                  setSelected(new Set(checked ? adjustableRows.map((row) => row.adjustable!) : []))
                }
              />
            </TableHead>
          ) : null}
          <TableHead className="min-w-56">{nameHeader}</TableHead>
          <TableHead className="whitespace-nowrap">{text("columns.strategy")}</TableHead>
          <TableHead className="whitespace-nowrap text-right">
            {text("columns.adjustment")}
          </TableHead>
          {METRIC_COLUMNS.map((column) => (
            <TableHead key={column} className="whitespace-nowrap text-right">
              {text(`columns.${column}`)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} className="group hover:bg-table-row-hover">
            {editable && subTab !== "audience" ? (
              <TableCell>
                <Checkbox
                  aria-label={text("edit.selectRow", { name: row.name })}
                  // Off-Amazon cannot be adjusted, so it cannot be picked either.
                  disabled={!row.adjustable}
                  checked={!!row.adjustable && selected.has(row.adjustable)}
                  onCheckedChange={(checked) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (!row.adjustable) return current;
                      if (checked) next.add(row.adjustable);
                      else next.delete(row.adjustable);
                      return next;
                    })
                  }
                />
              </TableCell>
            ) : null}
            <TableCell>
              <span className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-ink" title={row.name}>
                  {row.name}
                </span>
                {row.placement ? (
                  <ControlButton
                    variant="ghost"
                    controlSize="icon-toolbar"
                    aria-label={text("detailOf", { name: row.name })}
                    title={text("detail")}
                    onClick={() => {
                      setDetail(row.placement ?? null);
                      setLastDetail(row.placement ?? null);
                    }}
                  >
                    <IconChartLine />
                  </ControlButton>
                ) : null}
              </span>
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <span className="flex items-center gap-1">
                <CampaignBiddingStrategyCell strategy={data?.biddingStrategy ?? null} />
                {editable && row.adjustable ? (
                  <span className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <BiddingStrategyPopover
                      strategy={data?.biddingStrategy ?? null}
                      pending={save.isPending}
                      onSubmit={(strategy) => save.mutateAsync({ strategy })}
                    />
                  </span>
                ) : null}
              </span>
            </TableCell>
            <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">
              <span className="flex items-center justify-end gap-1">
                {adjustmentText(row.adjustment)}
                {editable && row.adjustable ? (
                  <ControlButton
                    variant="ghost"
                    controlSize="icon-toolbar"
                    className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label={text("edit.adjustOf", { name: row.name })}
                    title={text("edit.singleTitle")}
                    onClick={() =>
                      setEditing({
                        open: true,
                        title: text("edit.singleTitle"),
                        batch: false,
                        fields: [fieldOf(row)],
                      })
                    }
                  >
                    <IconPencil />
                  </ControlButton>
                ) : null}
              </span>
            </TableCell>
            {METRIC_COLUMNS.map((column) => (
              <TableCell
                key={column}
                className="whitespace-nowrap text-right font-mono tabular-nums"
              >
                {metricText(row.metrics, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {total ? (
        <TableFooter>
          <TableRow>
            {editable && subTab !== "audience" ? <TableCell /> : null}
            <TableCell className="font-semibold">{text("total")}</TableCell>
            <TableCell />
            <TableCell />
            {METRIC_COLUMNS.map((column) => (
              <TableCell
                key={column}
                className="whitespace-nowrap text-right font-mono tabular-nums"
              >
                {metricText(total, column)}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      ) : null}
    </DataTable>
  );

  return (
    <div className="flex flex-col gap-3">
      <SegmentControl
        size="compact"
        aria-label={text("title")}
        value={subTab}
        onValueChange={(value) => {
          setSubTab(value as SubTab);
          setSearch("");
          setSelected(new Set());
        }}
      >
        {SUB_TABS.map((value) => (
          <SegmentControlItem key={value} value={value}>
            {text(`subTabs.${value}`)}
          </SegmentControlItem>
        ))}
      </SegmentControl>
      <CampaignDetailPerformance
        campaignId={campaignId}
        shopId={shopId}
        range={range}
        timeZone={timeZone}
        collapseStorageKey="adpilot:campaign-bid-adjustment-chart:v1"
      />
      <section aria-label={text(`subTabs.${subTab}`)} aria-busy={query.isFetching}>
        <DataTableFrame toolbar={toolbar}>{body}</DataTableFrame>
      </section>
      <PlacementAdjustmentDialog
        open={editing?.open ?? false}
        onOpenChange={(open) =>
          !open && setEditing((current) => (current ? { ...current, open: false } : current))
        }
        title={editing?.title ?? ""}
        batch={editing?.batch ?? false}
        fields={editing?.fields ?? []}
        pending={save.isPending}
        onSubmit={(placements) => save.mutate({ placements })}
      />
      <PlacementDetailDialog
        campaignId={campaignId}
        open={detail !== null}
        placement={lastDetail}
        name={lastDetail ? text(`placements.${lastDetail}`) : ""}
        dates={dates}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
